import { cn } from '@woonstad/helpers-utils';
import { forwardRef, InputHTMLAttributes, useMemo } from 'react';

import { labelRegularClasses } from '../../typography/Typography';
import { useFormField } from '../FormField';
import { sharedInputClasses } from '../shared-input';

interface Props {
  transformValue?: (value: string | undefined) => string | undefined;
  mask?: Array<RegExp | string>;
}

type MaskResult = { formattedValue: string; valueIndex: number } | null;

function applyMaskCharacter(pattern: RegExp | string, char: string | undefined, currentFormatted: string, valueIndex: number): MaskResult {
  if (typeof pattern === 'string') {
    const nextIndex = pattern === char ? valueIndex + 1 : valueIndex;
    return { formattedValue: currentFormatted + pattern, valueIndex: nextIndex };
  }

  if (pattern instanceof RegExp) {
    if (char && pattern.test(char)) {
      return { formattedValue: currentFormatted + char, valueIndex: valueIndex + 1 };
    }
    return null;
  }

  return null;
}

function formatValueOnDelete(value: string): string {
  return value;
}

function formatValueOnInsert(value: string, mask: Array<RegExp | string>): string {
  let formattedValue = '';
  let valueIndex = 0;

  for (const pattern of mask) {
    const result = applyMaskCharacter(pattern, value[valueIndex], formattedValue, valueIndex);
    if (!result) break;
    formattedValue = result.formattedValue;
    valueIndex = result.valueIndex;
  }

  return formattedValue;
}

export const Text = forwardRef<HTMLInputElement, Props & InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const {
    transformValue,
    mask,
    ...restProps
  } = props;

  const {
    field,
    fieldState,
    name,
    formItemId,
  } = useFormField();

  const inputClasses = cn(
    sharedInputClasses, labelRegularClasses, 'text-input-activated placeholder:text-input-enabled',
    fieldState.error && 'border-input-error',
    // Opacity is needed for iOS mobile styling
    (field?.disabled || props.disabled || props.readOnly) && 'border-none bg-input-disabled text-input-disabled opacity-100',
  );

  const formatValue = (currentValue: string) => {
    if (!mask) return currentValue;

    if (currentValue.length < (field?.value?.length ?? 0)) {
      return formatValueOnDelete(currentValue);
    }

    return formatValueOnInsert(currentValue, mask);
  };

  const handleChangeInternal = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatValue(event.target.value);
    const transformedValue = transformValue ? transformValue(formattedValue) : formattedValue;

    field?.onChange(transformedValue);
  };

  const fieldValue = useMemo(() => {
    if (transformValue) {
      return transformValue(field?.value);
    }

    return field?.value;
  }, [field?.value, transformValue]);

  return (
    <input
      ref={ref}
      {...restProps}
      id={formItemId}
      className={cn(inputClasses, props.className)}
      disabled={field?.disabled || props.disabled}
      name={name}
      value={fieldValue}
      onBlur={(ev) => {
        if (props.onBlur) props.onBlur(ev);
        field?.onBlur();
      }}
      onChange={(ev) => {
        if (props.onChange) props.onChange(ev);
        handleChangeInternal(ev);
      }}
    />
  );
});

Text.displayName = 'Text';
